import React from 'react';

export interface GamutLoaderProps {
    /**
     * - `page`: Full-screen branded GAMUT loader (dark background, centered)
     * - `section`: Section-level loader in the "GAMUT" brand style (centered)
     * - `inline`: Compact inline loader used for per-pane loading text
     */
    variant?: 'page' | 'section' | 'inline';
    /** Displayed text. Defaults to "GAMUT" for page/section variants, "Loading..." for inline. */
    label?: string;
    /** Extra classes on the root element (optional). */
    className?: string;
}

/**
 * Unified branded loader used across the app. Replaces the several inline
 * `<div className="... text-accent-electric animate-pulse">GAMUT</div>` and
 * `<div className="p-8 text-accent-electric animate-pulse">Loading...</div>`
 * copies.
 */
export const GamutLoader: React.FC<GamutLoaderProps> = ({
    variant = 'page',
    label,
    className = '',
}) => {
    if (variant === 'page') {
        return (
            <div
                className={`min-h-screen bg-bg-primary flex items-center justify-center text-accent-electric font-bold tracking-tighter text-2xl animate-pulse ${className}`.trim()}
            >
                {label ?? 'GAMUT'}
            </div>
        );
    }

    if (variant === 'section') {
        return (
            <div
                className={`text-accent-electric font-bold tracking-tighter text-2xl animate-pulse ${className}`.trim()}
            >
                {label ?? 'GAMUT'}
            </div>
        );
    }

    // inline: caller provides padding via className (defaults to p-8)
    const pad = className && /\bp-\d/.test(className) ? '' : 'p-8';
    return (
        <div className={`${pad} text-accent-electric animate-pulse ${className ?? ''}`.trim()}>
            {label ?? 'Loading...'}
        </div>
    );
};

/**
 * Convenience wrapper matching the common `<div className="p-8 text-accent-electric animate-pulse">Loading ...</div>` pattern.
 */
export const InlineLoading: React.FC<{ label?: string; className?: string }> = ({
    label = 'Loading...',
    className,
}) => <GamutLoader variant="inline" label={label} className={className} />;

export default GamutLoader;
